import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  styled,
  Switch,
  Dialog,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Import Close Icon
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import CheckIcon from '@mui/icons-material/Check';
import { StyledTypography } from "../../assets/css/styledComponent";
import { GET_PLANS } from "../../graphql/queries";
import { UNSUBSCRIBE } from "../../graphql/mutations";
import { UserContext } from "../../components/auth/auth-context";
import SubscriptionButton from "../../components/payment plans/subscriptionButton";
import { planEnv } from "../../util/link"; 
// Styled components
const PricingContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const PricingCard = styled(Box)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  maxWidth: 360,
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
}));

const PricingCardHeader = styled(Box)(({ theme }) => ({
  borderBottom: "2px solid",
  borderColor: theme.palette.primary.light,
  paddingBottom: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const PricingCardFeatures = styled("ul")({
  paddingLeft: 0,
  listStyleType: "none",
  margin: 0,
});

const Feature = styled("li")({
  display: "flex",
  alignItems: "center",
  marginBottom: 12,
  color: "#555",
  gap: "10px"
});



const PricingPage = () => { 
  const location = useLocation();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const isXsScreen = useMediaQuery("(max-width:700px)");
  const { data } = useQuery(GET_PLANS, {
    variables: { planEnv: planEnv }
  });
  const [isYearlySubscription, setIsYearlySubscription] = useState(false);

  const [unsubscribe] = useMutation(UNSUBSCRIBE);
  const [open, setOpen] = useState(true); // Dialog open state

  const rawRedirectUrl = location.state?.from || null;

  // Function to extract path from the full URL
  const getPathFromUrl = (url) => {
    if (!url) return null;
    const urlObj = new URL(url);
    return urlObj.pathname; // Returns the path part
  };

  // Extract the clean path
  const redirectUrl = getPathFromUrl(rawRedirectUrl);

  const handleSubscriptionChange = () => {
    setIsYearlySubscription(!isYearlySubscription);
  };

  const handleClose = () => {
    setOpen(false);
    navigate(redirectUrl);
  };
  const handleUnsubscribe = async () => {
    const confirm = window.confirm("Are you sure you want to unsubscribe?");
    if (!confirm) {
      return;
    }
    try {
      const response = await unsubscribe({
        variables: {
          userId: providerUser.userId,
          subscriptionID: providerUser.subscriptionID,
        },
      });

      if (response.data.unsubscribe.success) {
        localStorage.setItem("token", response.data.unsubscribe.token);
        window.location.reload();
      } else {
        alert("Failed to cancel subscription. Please try again.");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("An error occurred while cancelling the subscription.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="body"
      PaperProps={{
        sx: {
          margin: 1,
          borderRadius: 2,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          margin: "auto", 
          backgroundColor: "#f4f4f4", 
          borderRadius: 2,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 4 },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 2,
            right: 10,
            color: "#333",
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <Typography
            variant={isXsScreen ? "h3" : "h2"}
            fontWeight={800}
            align="center"
            style={{
              color: "#33475B",
              letterSpacing: "1.2px",
            }}
          >
            Flexible, Value-Packed Plans
          </Typography>

          <Typography
            variant="h6"
            fontWeight={500}
            align="center"
            style={{
              color: "#6B7A8F",
              maxWidth: "650px",
              lineHeight: 1.5,
              marginTop: "8px",
            }}
          >
            Find the plan that works best for you. Enjoy the flexibility to adjust as your needs change — upgrade, downgrade, or pause anytime.
          </Typography>

          <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
            <Typography variant="body1" style={{ color: "#33475B", fontWeight: 600 }}>
              Monthly
            </Typography>
            <Switch
              checked={isYearlySubscription}
              onChange={handleSubscriptionChange}
              color="warning"
            />
            <Typography variant="body1" style={{ color: "#33475B", fontWeight: 600 }}>
              Yearly
            </Typography>
          </Box>
        </Box>

        <PricingContainer>
          {data?.plans?.map((plan) => {
            const price = isYearlySubscription
              ? plan.priceYearly
              : plan.priceMonthly;
            const accept = Number(providerUser?.planId) === Number(plan.id);
            let parsedFeatures;

            try {
              // Assuming plan.features is a valid JSON string
              // Try parsing directly without manipulating the string
              parsedFeatures = JSON.parse(plan.features);
            } catch (error) {
              console.error("Error parsing features:", error);
              parsedFeatures = {}; // Default to an empty object on error
            }

            return (
              <PricingCard key={plan.name}>
                <PricingCardHeader>
                  <Typography variant="h5" gutterBottom color="#333">
                    {plan.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    color={"#4caf50"}
                  >
                    {plan.name !== "Free" && (
                      <>
                        {price} {isYearlySubscription ? "per user/year" : "per user/month"}
                      </>
                    )}

                  </Typography>
                </PricingCardHeader>
                <PricingCardFeatures>
                  {Object.entries(parsedFeatures).map(
                    ([index, featureName]) => (
                      <Feature key={featureName}>
                        <CheckIcon />
                        <StyledTypography>{featureName}</StyledTypography>
                      </Feature>
                    )
                  )}
                </PricingCardFeatures>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
                  {providerUser ? (
                    plan.name === "Free" ? null : accept ? (
                      <Button variant="contained" color="secondary" disabled>
                        Current Plan ({providerUser?.subscriptionPlanType})
                      </Button>
                    ) : (
                      <SubscriptionButton
                          plans={data?.plans}
                          selectedPlanId={plan.id}
                        duration={isYearlySubscription ? "Yearly" : "Monthly"}
                          token={providerUser}
                      />
                    )
                  ) : (
                    <Button variant="outlined" color="primary">Sign Up</Button>
                  )}
                </Box>
              </PricingCard>
            );
          })}
        </PricingContainer>
      </Box>
    </Dialog>
  );
};

export default PricingPage;
